/**
 * News Dashboard Page
 * Aggregated news feed from all sources with AI sentiment analysis
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { newsAPI } from '../services/api';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import {
  RefreshCw, TrendingUp, TrendingDown, ExternalLink, AlertTriangle,
  Newspaper, Zap, Filter, Search, Bell, Activity, BarChart3,
  Clock, CheckCircle, XCircle, Flame, Bitcoin, DollarSign,
  ChevronDown, ChevronUp, Target, Users
} from 'lucide-react';

// Sentiment Badge Component
const SentimentBadge = ({ score, label, size = 'normal' }) => {
  const getColor = () => {
    if (score >= 70) return 'bg-accent-green text-white';
    if (score >= 55) return 'bg-accent-green/20 text-accent-green';
    if (score <= 30) return 'bg-accent-red text-white';
    if (score <= 45) return 'bg-accent-red/20 text-accent-red';
    return 'bg-text-muted/20 text-text-muted';
  };

  const getIcon = () => {
    if (score >= 55) return <TrendingUp className="w-3 h-3" />;
    if (score <= 45) return <TrendingDown className="w-3 h-3" />;
    return null;
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getColor()} ${size === 'small' ? 'text-[10px] px-1.5 py-0.5' : ''}`}>
      {getIcon()}
      {score}
    </span>
  );
};

// Recommendation Badge
const RecommendationBadge = ({ recommendation }) => {
  if (!recommendation) return null;
  
  const colors = {
    BUY: 'bg-accent-green text-white',
    SELL: 'bg-accent-red text-white',
    HOLD: 'bg-text-muted/30 text-text-secondary',
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-bold ${colors[recommendation.action] || colors.HOLD}`}>
      {recommendation.action}
    </span>
  );
};

// Asset Tag Component
const AssetTag = ({ symbol, type }) => {
  const colors = {
    crypto: 'bg-accent-yellow/15 text-accent-yellow border-accent-yellow/30',
    stock: 'bg-accent-blue/15 text-accent-blue border-accent-blue/30',
    options: 'bg-accent-purple/15 text-accent-purple border-accent-purple/30',
  };

  const icons = {
    crypto: <Bitcoin className="w-3 h-3" />,
    stock: <BarChart3 className="w-3 h-3" />,
    options: <Target className="w-3 h-3" />,
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-mono ${colors[type] || colors.stock}`}>
      {icons[type]}
      {symbol}
    </span>
  );
};

// News Item Card
const NewsCard = ({ item }) => {
  const [expanded, setExpanded] = useState(false);
  const timeAgo = getTimeAgo(item.timestamp);

  return (
    <div className={`card p-4 hover:bg-bg-tertiary/50 transition-all ${item.isInfluencerAlert ? 'border-l-4 border-l-accent-orange' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs text-accent-blue font-medium">{item.source}</span>
            <span className="text-text-muted">•</span>
            <span className="text-xs text-text-muted">{timeAgo}</span>
            {item.isInfluencerAlert && (
              <span className="px-1.5 py-0.5 bg-accent-orange/15 text-accent-orange text-[10px] rounded flex items-center gap-1">
                <Users className="w-3 h-3" />
                Influencer Alert
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="font-medium text-sm mb-2 line-clamp-2">{item.title}</h3>

          {/* Assets */}
          {item.assets && item.assets.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {item.assets.slice(0, 5).map((asset, i) => (
                <AssetTag key={i} symbol={asset.symbol} type={asset.type} />
              ))}
              {item.assets.length > 5 && (
                <span className="text-xs text-text-muted">+{item.assets.length - 5} more</span>
              )}
            </div>
          )}

          {/* Description (expandable) */}
          {expanded && item.description && (
            <p className="text-xs text-text-secondary mb-2">{item.description}</p>
          )}

          {/* Influencer Info */}
          {item.influencers && item.influencers.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {item.influencers.map((inf, i) => (
                <span key={i} className="text-xs bg-accent-purple/15 text-accent-purple px-2 py-0.5 rounded">
                  {inf.name} ({inf.handle})
                </span>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-text-muted hover:text-text-primary flex items-center gap-1"
            >
              {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {expanded ? 'Less' : 'More'}
            </button>
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-accent-blue hover:underline flex items-center gap-1"
            >
              Read <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Right Side - Sentiment & Recommendation */}
        <div className="flex flex-col items-end gap-2">
          <SentimentBadge score={item.sentiment?.score || 50} label={item.sentiment?.label} />
          <RecommendationBadge recommendation={item.recommendation} />
          {item.impact && (
            <span className="text-xs text-text-muted">
              Impact: {item.impact}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Market Sentiment Gauge
const SentimentGauge = ({ sentiment }) => {
  if (!sentiment) return null;

  const getGaugeColor = (score) => {
    if (score >= 60) return '#00ff88';
    if (score <= 40) return '#ff3366';
    return '#fbbf24';
  };

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
        <Activity className="w-5 h-5 text-accent-blue" />
        Market Sentiment
      </h3>
      
      <div className="flex items-center justify-center mb-4">
        <div className="relative w-32 h-32">
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke="#1a1a24"
              strokeWidth="12"
              fill="none"
            />
            <circle
              cx="64"
              cy="64"
              r="56"
              stroke={getGaugeColor(sentiment.score)}
              strokeWidth="12"
              fill="none"
              strokeDasharray={`${(sentiment.score / 100) * 352} 352`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold">{sentiment.score}</span>
            <span className="text-xs text-text-muted capitalize">{sentiment.overall?.replace('_', ' ')}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="p-2 bg-bg-tertiary rounded">
          <div className="text-text-muted">Crypto</div>
          <div className="font-bold">{sentiment.breakdown?.crypto?.avgScore || '-'}</div>
        </div>
        <div className="p-2 bg-bg-tertiary rounded">
          <div className="text-text-muted">Stocks</div>
          <div className="font-bold">{sentiment.breakdown?.stock?.avgScore || '-'}</div>
        </div>
        <div className="p-2 bg-bg-tertiary rounded">
          <div className="text-text-muted">Social</div>
          <div className="font-bold">{sentiment.breakdown?.social?.avgScore || '-'}</div>
        </div>
      </div>

      <div className="mt-4 text-xs text-text-muted text-center">
        Based on {sentiment.articleCount || 0} articles
      </div>
    </div>
  );
};

// Trending Assets Card
const TrendingCard = ({ trending }) => {
  if (!trending || trending.length === 0) return null;

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
        <Flame className="w-5 h-5 text-accent-orange" />
        Trending Assets
      </h3>
      
      <div className="space-y-2">
        {trending.slice(0, 8).map((item, i) => (
          <div key={i} className="flex items-center justify-between p-2 bg-bg-tertiary rounded hover:bg-bg-card transition-all">
            <div className="flex items-center gap-2">
              <span className="text-text-muted text-xs w-4">#{i + 1}</span>
              <AssetTag symbol={item.symbol} type={item.type} />
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-text-muted">{item.mentions} mentions</span>
              <SentimentBadge score={item.avgSentiment} size="small" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Alerts Card
const AlertsCard = ({ alerts, onViewAll }) => {
  if (!alerts || alerts.length === 0) return null;

  const alertColors = {
    critical: 'border-l-accent-red bg-accent-red/5',
    high: 'border-l-accent-orange bg-accent-orange/5',
    medium: 'border-l-accent-yellow bg-accent-yellow/5',
    low: 'border-l-text-muted',
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <Bell className="w-5 h-5 text-accent-red" />
          High Impact Alerts
        </h3>
        {onViewAll && (
          <button onClick={onViewAll} className="text-xs text-accent-blue hover:underline">
            View All
          </button>
        )}
      </div>
      
      <div className="space-y-2">
        {alerts.slice(0, 5).map((alert, i) => (
          <a
            key={i}
            href={alert.link}
            target="_blank"
            rel="noopener noreferrer"
            className={`block p-3 rounded border-l-4 ${alertColors[alert.alertLevel]} hover:opacity-80 transition-all`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-text-muted mb-1">{alert.source}</div>
                <div className="text-sm font-medium line-clamp-2">{alert.title}</div>
                {alert.assets && alert.assets.length > 0 && (
                  <div className="flex gap-1 mt-1">
                    {alert.assets.slice(0, 3).map((asset, j) => (
                      <span key={j} className="text-[10px] font-mono text-text-secondary">
                        {asset.symbol}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${
                alert.alertLevel === 'critical' ? 'bg-accent-red text-white' :
                alert.alertLevel === 'high' ? 'bg-accent-orange text-white' :
                'bg-text-muted/30 text-text-secondary'
              }`}>
                {alert.alertLevel}
              </span>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

// Helper function
const getTimeAgo = (timestamp) => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

// Main News Dashboard
const NewsDashboard = () => {
  const { hasAlpacaKeys } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data state
  const [marketSummary, setMarketSummary] = useState(null);
  const [newsFeed, setNewsFeed] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [trending, setTrending] = useState([]);

  // Filter state
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSentiment, setFilterSentiment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [maxAge, setMaxAge] = useState(24);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, alertsRes, trendingRes] = await Promise.all([
        newsAPI.getMarketSummary().catch(() => null),
        newsAPI.getAlerts(70, 6).catch(() => null),
        newsAPI.getTrending(6).catch(() => null),
      ]);

      if (summaryRes?.data) {
        setMarketSummary(summaryRes.data);
        setNewsFeed(summaryRes.data.recentNews || []);
      }
      if (alertsRes?.data) setAlerts(alertsRes.data.alerts || []);
      if (trendingRes?.data) setTrending(trendingRes.data.trending || []);
    } catch (err) {
      console.error('Failed to fetch news data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch more news
  const fetchMoreNews = async () => {
    try {
      const res = await newsAPI.getFeed(maxAge, 100);
      if (res?.data?.news) {
        setNewsFeed(res.data.news);
      }
    } catch (err) {
      console.error('Failed to fetch more news:', err);
    }
  };

  // Refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('News refreshed');
  };

  // Initial fetch
  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Filter news
  const filteredNews = newsFeed.filter(item => {
    // Category filter
    if (filterCategory !== 'all' && item.category !== filterCategory) return false;
    
    // Sentiment filter
    if (filterSentiment === 'bullish' && (item.sentiment?.score || 50) < 55) return false;
    if (filterSentiment === 'bearish' && (item.sentiment?.score || 50) > 45) return false;
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = item.title?.toLowerCase().includes(query);
      const matchesAsset = item.assets?.some(a => a.symbol.toLowerCase().includes(query));
      if (!matchesTitle && !matchesAsset) return false;
    }
    
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation title="News Dashboard" subtitle="Aggregated market intelligence" />
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-accent-blue" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation 
        title="News Dashboard" 
        subtitle="Aggregated market intelligence"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />
      
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {/* Top Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <SentimentGauge sentiment={marketSummary?.marketSentiment} />
          <TrendingCard trending={trending} />
          <AlertsCard alerts={alerts} />
        </div>

        {/* Market Summary */}
        {marketSummary?.summary && (
          <div className="card p-4 mb-6 bg-accent-blue/5 border border-accent-blue/20">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-accent-blue mt-0.5" />
              <div>
                <h4 className="font-semibold text-sm mb-1">AI Market Summary</h4>
                <p className="text-sm text-text-secondary">{marketSummary.summary}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex items-center gap-2 px-3 py-2 bg-bg-tertiary rounded-lg flex-1 min-w-[200px] max-w-xs">
              <Search className="w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Search news or symbols..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none text-text-primary placeholder:text-text-muted w-full text-sm"
              />
            </div>

            {/* Category Filter */}
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2 bg-bg-tertiary rounded-lg text-sm border border-border-color"
            >
              <option value="all">All Categories</option>
              <option value="crypto">Crypto</option>
              <option value="stock">Stocks</option>
              <option value="options">Options</option>
            </select>

            {/* Sentiment Filter */}
            <select
              value={filterSentiment}
              onChange={(e) => setFilterSentiment(e.target.value)}
              className="px-3 py-2 bg-bg-tertiary rounded-lg text-sm border border-border-color"
            >
              <option value="all">All Sentiment</option>
              <option value="bullish">Bullish Only</option>
              <option value="bearish">Bearish Only</option>
            </select>

            {/* Time Filter */}
            <select
              value={maxAge}
              onChange={(e) => { setMaxAge(parseInt(e.target.value)); fetchMoreNews(); }}
              className="px-3 py-2 bg-bg-tertiary rounded-lg text-sm border border-border-color"
            >
              <option value={6}>Last 6 hours</option>
              <option value={12}>Last 12 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={48}>Last 48 hours</option>
            </select>

            <button
              onClick={fetchMoreNews}
              className="btn-secondary text-sm px-4 py-2"
            >
              <Filter className="w-4 h-4 mr-1 inline" />
              Apply
            </button>
          </div>
        </div>

        {/* News Feed */}
        <div className="space-y-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Newspaper className="w-5 h-5 text-text-muted" />
              News Feed
              <span className="text-sm font-normal text-text-muted">({filteredNews.length} articles)</span>
            </h3>
          </div>

          {filteredNews.length > 0 ? (
            filteredNews.map((item, i) => (
              <NewsCard key={item.id || i} item={item} />
            ))
          ) : (
            <div className="card p-8 text-center">
              <Newspaper className="w-10 h-10 mx-auto text-text-muted mb-3 opacity-50" />
              <p className="text-text-muted">No news articles match your filters</p>
              <button 
                onClick={() => { setFilterCategory('all'); setFilterSentiment('all'); setSearchQuery(''); }}
                className="text-accent-blue text-sm mt-2 hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>

        {/* Sources Footer */}
        <div className="mt-8 pt-4 border-t border-border-color">
          <div className="text-xs text-text-muted">
            <span className="font-medium">Sources: </span>
            Reddit WSB, CoinDesk, The Block, Bitcoin Magazine, Cointelegraph, Decrypt, CryptoSlate, CryptoNews, U.Today, BeInCrypto
          </div>
          {marketSummary?.lastUpdated && (
            <div className="text-xs text-text-muted mt-1">
              Last updated: {new Date(marketSummary.lastUpdated).toLocaleString()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsDashboard;
